import React, { useState, useContext, useRef, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import {
  Container,
  Typography,
  Box,
  OutlinedInput,
  Button,
  Card,
  CardMedia,
  CardContent,
  Divider,
  IconButton,
} from "@mui/material";
import { PersonOutline, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { UserContext } from "../../../components/auth/auth-context";
import easyScheduling from "../../../assets/images/easyScheduling.png";
import customizableService from "../../../assets/images/customizableService.png";
import multipleStaffManagement from "../../../assets/images/multipleStaffManagement.png";
import multiBusinessManagement from "../../../assets/images/multiBusinessManagement.png";
import securePayment from "../../../assets/images/securePayment.png";
import tippingFeature from "../../../assets/images/tippingFeature.png";
import businessAndPersonalPages from "../../../assets/images/businessAndPersonalPages.png";
import realTimeNotification from "../../../assets/images/realTimeNotification.png";
import { useIsMobile } from "../../../util/utils";

/**
 * BusinessLandingPage component
 * - Top jumbotron remains static
 * - Continuous looping carousel without pauses
 * - Arrow nav clamps to content bounds to avoid empty slides
 */
const BusinessLandingPage: React.FC = () => {
  const userContext = useContext(UserContext);
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const offsetRef = useRef(0); // current animation x offset
  const controls = useAnimation();
  const features = [
    {
      title: "Easy Scheduling",
      description: "Book appointments online 24/7 with an intuitive interface.",
      image: easyScheduling,
      alt: "Easy Scheduling",
    },
    {
      title: "Customizable Services",
      description:
        "Add service details, set pricing, and manage availability easily.",
      image: customizableService,
      alt: "Customizable Services",
    },
    {
      title: "Multiple Staff Management",
      description:
        "Assign bookings to team members and track schedules effortlessly.",
      image: multipleStaffManagement,
      alt: "Multiple Staff Management",
    },
    {
      title: "Multi-Business Management",
      description: "Manage multiple branches or industries under one account.",
      image: multiBusinessManagement,
      alt: "Multi-Business Management",
    },
    {
      title: "Real-Time Notifications",
      description:
        "Instant alerts for new bookings, cancellations, and changes.",
      image: realTimeNotification,
      alt: "Real-Time Notifications",
    },
    {
      title: "Secure Payments",
      description: "Integrate gateways like PayPal for safe transactions.",
      image: securePayment,
      alt: "Secure Payments",
    },
    {
      title: "Tipping Feature",
      description: "Collect tips easily via shareable links to boost revenue.",
      image: tippingFeature,
      alt: "Tipping Feature",
    },
    {
      title: "Business & Personal Pages",
      description:
        "Auto-generate pages tailored to business or personal branding.",
      image: businessAndPersonalPages,
      alt: "Business & Personal Pages",
    },
  ];

  // sizing constants
  const CARD_W = 300;
  const GAP = 16;
  const total = CARD_W + GAP;
  const count = features.length;
  const wrapperWidth = total * count * 2;
  const dx = -total * count; // total translation distance for one loop
  const navStep = total * 2; // scroll step for arrow nav

  // Email submit handler
  const handleEmailSubmit = () => {
    if (/^\S+@\S+\.\S+$/.test(email)) {
      window.location.href = `/signup?email=${encodeURIComponent(email)}`;
    } else {
      setError(true);
    }
  };
  // start continuous loop on mount
  useEffect(() => {
    controls.set({ x: offsetRef.current });
    controls.start({
      x: [offsetRef.current, offsetRef.current + dx],
      transition: { repeat: Infinity, duration: count * 10, ease: "linear" },
    });
  }, [controls, dx, count]);

  // advance by one slide width, wrap offset
  const handleNext = () => {
    controls.stop();
    let nextOffset = offsetRef.current - total;
    if (nextOffset < dx) nextOffset += -dx; // wrap back into [dx,0]
    offsetRef.current = nextOffset;
    controls.set({ x: offsetRef.current });
    controls.start({
      x: [offsetRef.current, offsetRef.current + dx],
      transition: { repeat: Infinity, duration: count * 10, ease: "linear" },
    });
  };

  // go back by one slide
  const handlePrev = () => {
    controls.stop();
    let prevOffset = offsetRef.current + total;
    if (prevOffset > 0) prevOffset += dx; // wrap into [dx,0]
    offsetRef.current = prevOffset;
    controls.set({ x: offsetRef.current });
    controls.start({
      x: [offsetRef.current, offsetRef.current + dx],
      transition: { repeat: Infinity, duration: count * 10, ease: "linear" },
    });
  };

  return (
    <Box sx={{ background: "white" }}>
      {/* Top Jumbotron Section (static) */}
      <Box
        sx={{
          minHeight: isMobile ? "auto" : "500px",
          marginTop: userContext.providerUser ? "40px" : "30px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              width: isMobile ? "100%" : "70%",
              padding: "60px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            {/* Welcome Section */}
            <Box
              sx={{ textAlign: userContext.providerUser ? "left" : "center" }}
            >
              <Typography
                variant="h3"
                sx={{
                  margin: 0,
                  fontSize: `${
                    45 -
                    (userContext.providerUser?.username?.split(" ")[0]
                      ?.length || 0) *
                      1.3
                  }px`,
                }}
              >
                {userContext.providerUser?.username
                  ? `Welcome Back, ${
                      userContext.providerUser.username.split(" ")[0]
                    }`
                  : "Plany365"}
              </Typography>
              <Typography variant="h6">
                {userContext.providerUser ? (
                  <>
                    Plany365 makes it effortless to book services, pay securely,
                    and tip creators with PlanyCoin.
                    <br />
                    <br />
                    Whether you’re a business owner, professional, or booking
                    personally — <strong>create your profile</strong> with a
                    brand or personal slug to showcase offerings, manage
                    appointments, and receive support tips.
                  </>
                ) : (
                  <>
                    Plany365 is your all-in-one platform to book services,
                    handle payments, and enable PlanyCoin tipping — powered by a
                    flexible <strong>business-centric system</strong>.
                  </>
                )}
              </Typography>
            </Box>

            {/* Email Input for New Users */}
            {!userContext.providerUser.userId && (
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "20px",
                  alignItems: "center",
                }}
              >
                <OutlinedInput
                  value={email}
                  type="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(false);
                  }}
                  placeholder="Enter your email"
                  error={error}
                  fullWidth
                />
                <Button
                  onClick={handleEmailSubmit}
                  variant="contained"
                  sx={{ maxHeight: "51px" }}
                >
                  Get Started
                </Button>
              </Box>
            )}

            {/* My Account / Create Profile Buttons */}
            {userContext.providerUser.defaultBusinessId && (
              <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                <Button
                  startIcon={<PersonOutline />}
                  variant="contained"
                  sx={{ borderRadius: "25px" }}
                  onClick={() => (window.location.href = "/biz/bookingtypes")}
                >
                  My Account
                </Button>
              </Box>
            )}
            {userContext.providerUser.userId &&
              !userContext.providerUser.defaultBusinessId && (
                <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                  <Button
                    startIcon={<PersonOutline />}
                    variant="contained"
                    sx={{ borderRadius: "25px" }}
                    onClick={() => (window.location.href = "/biz/business")}
                  >
                    Create your first business profile
                  </Button>
                </Box>
              )}
          </Box>
        </Box>
      </Box>
      <Divider />

      {/* Carousel Section */}
      <Container maxWidth="lg" sx={{ py: 8, position: "relative" }}>
        <Typography variant="h4" align="center" gutterBottom>
          Key Features
        </Typography>

        <IconButton
          onClick={handlePrev}
          sx={{
            position: "absolute",
            left: -3,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.3,
            p: 0.5,
            zIndex: 2,
            "& svg": { fontSize: "2.5rem" },
            "&:hover": { opacity: 0.6 },
          }}
        >
          <ChevronLeft />
        </IconButton>

        <Box sx={{ overflow: "hidden" }}>
          <motion.div
            style={{ display: "flex", width: total * count * 2 }}
            animate={controls}
          >
            {[...features, ...features].map((feat, i) => (
              <Box key={i} sx={{ minWidth: CARD_W, mx: 1, flex: "0 0 auto" }}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={feat.image}
                    alt={feat.title}
                    height={240}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {feat.title}
                    </Typography>
                    <Typography variant="body2">{feat.description}</Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </motion.div>
        </Box>

        <IconButton
          onClick={handleNext}
          sx={{
            position: "absolute",
            right: -3,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.3,
            p: 0.5,
            zIndex: 2,
            "& svg": { fontSize: "2.5rem" },
            "&:hover": { opacity: 0.6 },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Container>
    </Box>
  );
};

export default BusinessLandingPage;
